import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

export default function ShowList() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  const showType = searchParams.get('type') || '';
  const isActive = searchParams.get('active') || 'all';
  const search = searchParams.get('search') || '';

  const fetchShows = async () => {
    try {
      const params = {};
      if (showType) params.show_type = showType;
      if (isActive !== 'all') params.is_active = isActive === 'true';
      if (search) params.search = search;

      const { data } = await api.get('/api/shows', { params });
      setShows(data);
    } catch (err) {
      console.error('[Shows] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShows();
  }, [showType, isActive, search]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/shows/${id}`);
      setShows(shows.filter((s) => s.id !== id));
    } catch (err) {
      console.error('[Shows] Delete failed:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchParams({ ...searchParams, search: e.target.value, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setSearchParams({ ...searchParams, [key]: value, page: 1 });
  };

  const toggleActive = async (show) => {
    try {
      await api.put(`/api/shows/${show.id}`, { is_active: !show.is_active });
      setShows(shows.map((s) => (s.id === show.id ? { ...s, is_active: !s.is_active } : s)));
    } catch (err) {
      console.error('[Shows] Toggle failed:', err);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-dark-400">Cargando series...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Series</h1>
        <Link to="/shows/create" className="btn btn-primary">
          + Nueva Serie
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Buscar por nombre o DJ..."
          value={search}
          onChange={handleSearch}
          className="input max-w-sm"
        />
        <select
          value={showType}
          onChange={(e) => handleFilterChange('type', e.target.value)}
          className="input w-40"
        >
          <option value="">Todos los tipos</option>
          <option value="radio">Radio show (música + jingles)</option>
          <option value="dj">DJ (sets)</option>
        </select>
        <select
          value={isActive}
          onChange={(e) => handleFilterChange('active', e.target.value)}
          className="input w-32"
        >
          <option value="all">Todos</option>
          <option value="true">Activo</option>
          <option value="false">Inactivo</option>
        </select>
      </div>

      {shows.length === 0 ? (
        <div className="py-12 text-center text-dark-400">No hay series. ¡Crea tu primera serie!</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>DJ</th>
                <th>Tipo</th>
                <th>Duración</th>
                <th>Estado</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {shows.map((show) => (
                <tr key={show.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      {show.image_url ? (
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-dark-800 border border-dark-700">
                          <img src={show.image_url} alt={show.name} className="h-full w-full object-cover" />
                        </div>
                      ) : (
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-dark-800 text-lg">
                          📻
                        </div>
                      )}
                      <Link to={`/shows/${show.id}`} className="font-medium text-dark-100 hover:text-primary-400">
                        {show.name}
                      </Link>
                    </div>
                  </td>
                  <td className="text-dark-300">{show.dj_name || '—'}</td>
                  <td>
                    <span className="capitalize">{show.show_type}</span>
                  </td>
                  <td>{show.duration_minutes ? `${show.duration_minutes} min` : '—'}</td>
                  <td>
                    <StatusBadge status={show.is_active ? 'active' : 'inactive'} />
                    {' '}
                    <button
                      onClick={() => toggleActive(show)}
                      className="text-xs text-dark-500 hover:text-dark-300"
                      title={show.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {show.is_active ? '●' : '○'}
                    </button>
                  </td>
                  <td className="text-right">
                    <Link
                      to={`/shows/${show.id}/schedule`}
                      className="text-xs text-primary-400 hover:text-primary-300"
                      title="Editor de programación semanal (arrastrá los slots)"
                    >
                      🗓️ Programación
                    </Link>
                    {' | '}
                    <Link to={`/shows/${show.id}/episodes`} className="text-xs text-dark-400 hover:text-dark-200">
                      Episodios
                    </Link>
                    {' | '}
                    <button
                      onClick={() => {
                        setConfirmItem(show);
                        setConfirmOpen(true);
                      }}
                      className="text-xs text-error-400 hover:text-error-300"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Serie"
        message={`¿Seguro que quieres eliminar "${confirmItem?.name}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={() => confirmItem && handleDelete(confirmItem.id)}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
